import { useMemo, useState } from "react";
import {
  Box,
  Paper,
  IconButton,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  TableSortLabel,
  Button as MUIButton,
  Pagination,
  Divider,
  Chip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import FilterListIcon from "@mui/icons-material/FilterList";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CircleIcon from "@mui/icons-material/Circle";
import { Typography } from "Atoms";
import CreateUserDialog from "./CreateUserModel";
import ConfirmDeleteDialog from "./ConfirmDeleteDialog";

type UserStatus = "alle" | "aktiv" | "inaktiv" | "gesperrt";
type UserType = "Administrator" | "Führungskraft" | "Mitarbeiter";

export interface UserRow {
  id: string;
  firstName: string;
  lastName: string;
  type: UserType;
  email: string;
  status: Exclude<UserStatus, "alle">; // concrete status, not "alle"
}

interface Props {
  rows: any;
  onCreate?: () => void;
  onEdit?: (row: UserRow) => void;
  onDelete?: (row: UserRow) => void;
}

/* --- helpers --------------------------------------------------------- */
const STATUS_COLOR: Record<
  Exclude<UserStatus, "alle">,
  "info" | "primary" | "warning"
> = {
  aktiv: "primary",
  inaktiv: "info",
  gesperrt: "info",
};

function applySearchFilterSort(
  rows: UserRow[],
  query: string,
  status: UserStatus,
  utype: UserType | "alle",
  orderBy: keyof UserRow,
  order: "asc" | "desc"
) {
  const q = query.trim().toLowerCase();

  let r = rows.filter((row) => {
    const matchesQ =
      !q ||
      [row.firstName, row.lastName, row.email, row.type].some((v) =>
        String(v).toLowerCase().includes(q)
      );
    const matchesStatus = status === "alle" ? true : row.status === status;
    const matchesType = utype === "alle" ? true : row.type === utype;
    return matchesQ && matchesStatus && matchesType;
  });

  r.sort((a, b) => {
    const A = String(a[orderBy] ?? "").toLowerCase();
    const B = String(b[orderBy] ?? "").toLowerCase();
    if (A < B) return order === "asc" ? -1 : 1;
    if (A > B) return order === "asc" ? 1 : -1;
    return 0;
  });

  return r;
}

/* --- component ------------------------------------------------------- */
export default function Users({ rows, onCreate, onEdit, onDelete }: Props) {
  // toolbar
  const [query, setQuery] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [status, setStatus] = useState<UserStatus>("alle");
  const [uType, setUType] = useState<UserType | "alle">("alle");

  // table sort
  const [orderBy, setOrderBy] = useState<keyof UserRow>("firstName");
  const [order, setOrder] = useState<"asc" | "desc">("asc");

  // pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);

  const [open, setOpen] = useState(false);

  const [openDelete, setOpenDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const filteredSorted = useMemo(
    () => applySearchFilterSort(rows, query, status, uType, orderBy, order),
    [rows, query, status, uType, orderBy, order]
  );

  const pageCount = Math.max(1, Math.ceil(filteredSorted.length / pageSize));
  const visible = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredSorted.slice(start, start + pageSize);
  }, [filteredSorted, page, pageSize]);

  const handleSort = (key: keyof UserRow) => {
    if (orderBy === key) setOrder((o) => (o === "asc" ? "desc" : "asc"));
    else {
      setOrderBy(key);
      setOrder("asc");
    }
  };

  return (
    <Paper sx={{ p: 5 }} elevation={4}>
      {/* Title + New + View icon */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={1}
      >
        <Typography variant="h6">
          Users <Chip label={rows.length} color="primary" size="small" sx={{ ml: 1 }} />
        </Typography>

        <Box display="flex" gap={1}>
          <MUIButton
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setOpen(true);
            }}
          >
             NEW
          </MUIButton>
        </Box>
      </Box>

      {/* Search + Filter toggle */}
      <Box sx={{ py: 2, flexWrap: "wrap" }}>
        <TextField
          placeholder="User Search..."
          size="small"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setPage(1);
          }}
          sx={{ minWidth: 320 }}
        />
        <MUIButton
          variant={filtersOpen ? "outlined" : "contained"}
          startIcon={<FilterListIcon />}
          onClick={() => setFiltersOpen((v) => !v)}
          sx={{ ml: 2 }}
        >
          FILTER
        </MUIButton>
      </Box>

      {/* Filters row */}
      {filtersOpen && (
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            mb: 2,
            display: "grid",
            gap: 2,
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          }}
        >
          <FormControl size="small">
            <InputLabel>User-Account-Status</InputLabel>
            <Select
              label="User-Account-Status"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value as UserStatus);
                setPage(1);
              }}
            >
              <MenuItem value="alle">alle</MenuItem>
              <MenuItem value="aktiv">aktiv</MenuItem>
              <MenuItem value="inaktiv">inaktiv</MenuItem>
              <MenuItem value="gesperrt">gesperrt</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small">
            <InputLabel>User Types</InputLabel>
            <Select
              label="User Types"
              value={uType}
              onChange={(e) => {
                setUType(e.target.value as any);
                setPage(1);
              }}
            >
              <MenuItem value="alle">alle</MenuItem>
              <MenuItem value="Administrator">Administrator</MenuItem>
              <MenuItem value="Führungskraft">Führungskraft</MenuItem>
              <MenuItem value="Mitarbeiter">Mitarbeiter</MenuItem>
            </Select>
          </FormControl>
        </Paper>
      )}

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 40 }} />
              <TableCell
                sortDirection={orderBy === "firstName" ? order : false}
              >
                <TableSortLabel
                  active={orderBy === "firstName"}
                  direction={orderBy === "firstName" ? order : "asc"}
                  onClick={() => handleSort("firstName")}
                >
                  Firstname
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === "lastName" ? order : false}>
                <TableSortLabel
                  active={orderBy === "lastName"}
                  direction={orderBy === "lastName" ? order : "asc"}
                  onClick={() => handleSort("lastName")}
                >
                  Lastname
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === "type" ? order : false}>
                <TableSortLabel
                  active={orderBy === "type"}
                  direction={orderBy === "type" ? order : "asc"}
                  onClick={() => handleSort("type")}
                >
                  User Type
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === "email" ? order : false}>
                <TableSortLabel
                  active={orderBy === "email"}
                  direction={orderBy === "email" ? order : "asc"}
                  onClick={() => handleSort("email")}
                >
                  E-Mail Address
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" sx={{ width: 96 }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {visible.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>
                  <CircleIcon
                    sx={{ height: "0.8rem", width: "0.8rem" }}
                    fontSize="small"
                    color={STATUS_COLOR[row.status]}
                  />
                </TableCell>
                <TableCell>{row.firstName}</TableCell>
                <TableCell>{row.lastName}</TableCell>
                <TableCell>{row.type}</TableCell>
                <TableCell>{row.email}</TableCell>
                <TableCell align="right">
                  {/* <IconButton
                    size="small"
                    onClick={() => onEdit?.(row)}
                    title="Bearbeiten"
                  >
                    <EditIcon fontSize="small" />
                  </IconButton> */}
                  <IconButton
                    size="small"
                    onClick={() => setOpenDelete(true)}
                    title="Löschen"
                  >
                    <DeleteOutlineIcon fontSize="small" color="error" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}

            {visible.length === 0 && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Box py={4} textAlign="center" color="text.secondary">
                    Keine Ergebnisse
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Divider sx={{ my: 1 }} />

      {/* Pagination + page size */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mt={1}
      >
        <Pagination
          page={page}
          count={pageCount}
          onChange={(_, p) => setPage(p)}
          color="primary"
        />
        <FormControl size="small" sx={{ width: 96 }}>
          <Select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
          >
            {[5, 10, 25, 50].map((n) => (
              <MenuItem key={n} value={n}>
                {n}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* create user modal here */}

      <CreateUserDialog
        open={open}
        onClose={() => setOpen(false)}
        onSave={async (payload:any) => {
          console.log("create user", payload);
          // call your API
          // await createUserAPI(payload)
          setOpen(false);
        }}
      />

      {/* Delete user modal here */}

      <ConfirmDeleteDialog
        open={openDelete}
        onCancel={() => setOpenDelete(false)}
        onConfirm={async () => {
          setDeleting(true);
          try {
            // await deleteUserAPI(userId);
          } finally {
            setDeleting(false);
            setOpenDelete(false);
          }
        }}
        itemName={``}
        title="Delete User"
        description="Are you sure you want to delete this user? This action cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        loading={deleting}
      />
    </Paper>
  );
}
